import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Crown, Star, Building2, MapPin, ArrowLeft, ExternalLink, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SponsorsPage = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isUSA } = useLocale();

  useEffect(() => {
    fetch(`${API_URL}/api/sponsors`)
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        setSponsors(data);
        setLoading(false);
      })
      .catch(() => {
        setSponsors([]);
        setLoading(false);
      });
  }, []);

  const SponsorCard = ({ sponsor }) => (
    <Link to={`/company/${sponsor.company_id}`}>
      <Card className="hover:shadow-lg transition-all cursor-pointer border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-amber-100">
        <CardContent className="p-6 text-center">
          <Badge className="bg-amber-500 text-white">
            <Star className="w-3 h-3 mr-1" />
            {isUSA ? 'Sponsor' : 'Patrocinador'}
          </Badge>
          
          <div className="w-32 h-20 mx-auto my-4 rounded-xl overflow-hidden bg-white shadow-sm flex items-center justify-center p-2">
            {(sponsor.logo || sponsor.logo_url) ? (
              <img src={sponsor.logo || sponsor.logo_url} alt={sponsor.company_name} className="max-w-full max-h-full object-contain" />
            ) : (
              <Building2 className="w-12 h-12 text-slate-400" />
            )}
          </div>
          
          <h3 className="font-semibold text-slate-900 text-lg">
            {sponsor.company_name || (isUSA ? 'Sponsor Company' : 'Empresa Patrocinadora')}
          </h3>
          
          {sponsor.location_state && (
            <p className="text-sm text-slate-500 flex items-center justify-center gap-1 mt-1">
              <MapPin className="w-3 h-3" />
              {sponsor.location_state}
            </p>
          )}
          
          {sponsor.description && (
            <p className="text-sm text-slate-600 mt-2 line-clamp-2">
              {sponsor.description}
            </p>
          )}
          
          <Button variant="outline" className="mt-4 text-amber-700 border-amber-300 hover:bg-amber-100">
            {isUSA ? 'View profile' : 'Ver perfil'}
            <ExternalLink className="w-3 h-3 ml-2" />
          </Button>
        </CardContent>
      </Card>
    </Link>
  );

  return (
    <div className="max-w-6xl mx-auto" data-testid="sponsors-page">
      {/* Back Button */}
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
        <ArrowLeft className="w-4 h-4" />
        {isUSA ? 'Back to Dashboard' : 'Volver al Panel'}
      </Link>

      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Crown className="w-8 h-8 text-amber-600" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope, sans-serif'}}>
          {isUSA ? 'Sponsors' : 'Patrocinadores'}
        </h1>
        <p className="text-slate-600 mt-2">
          {isUSA 
            ? 'Featured companies supporting the textile industry'
            : 'Empresas destacadas que apoyan la industria textil'}
        </p>
      </div>

      {/* Sponsors Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-slate-500 mt-4">{isUSA ? 'Loading sponsors...' : 'Cargando patrocinadores...'}</p>
        </div>
      ) : sponsors.length === 0 ? (
        <Card className="p-8 text-center">
          <Sparkles className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h3 className="font-semibold text-slate-900 text-lg mb-2">
            {isUSA ? 'Become a sponsor!' : '¡Conviértete en patrocinador!'}
          </h3>
          <p className="text-slate-600 mb-4">
            {isUSA 
              ? 'Get featured visibility and reach more buyers'
              : 'Obtén visibilidad destacada y alcanza más compradores'}
          </p>
          <Button className="bg-amber-500 hover:bg-amber-600 text-white">
            {isUSA ? 'Learn more' : 'Saber más'}
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sponsors.map(sponsor => (
            <SponsorCard key={sponsor.company_id} sponsor={sponsor} />
          ))}
        </div>
      )}

      {/* Become a Sponsor CTA */}
      {sponsors.length > 0 && (
        <Card className="mt-12 p-8 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-center">
          <h2 className="text-xl md:text-2xl font-bold mb-2">
            {isUSA ? 'Want to become a sponsor?' : '¿Quieres ser patrocinador?'}
          </h2>
          <p className="text-amber-100 mb-4">
            {isUSA 
              ? 'Get featured position and reach thousands of buyers'
              : 'Obtén posición destacada y alcanza miles de compradores'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="bg-white/20 rounded-lg px-6 py-3">
              <p className="text-2xl font-bold">$1,999</p>
              <p className="text-amber-100 text-sm">MXN/{isUSA ? 'month' : 'mes'}</p>
            </div>
          </div>
          <Button 
            className="mt-6 bg-white text-amber-600 hover:bg-amber-50 font-bold"
            onClick={() => {
              const message = encodeURIComponent('Hola, me interesa ser patrocinador en TexHub. ¿Me pueden dar más información?');
              window.location.href = `https://wa.me/525530063425?text=${message}`;
            }}
          >
            {isUSA ? 'Contact via WhatsApp' : 'Contactar por WhatsApp'}
          </Button>
        </Card>
      )}
    </div>
  );
};

export default SponsorsPage;
